/**
 * PageBackground Component
 *
 * Background gradient with SVG pattern that scrolls with page.
 * Used globally across all pages where Header is present.
 *
 * When `parallax` is enabled, the SVG pattern smoothly shifts following the
 * mouse cursor (mouse-based parallax). Used on the landing hero only.
 * Respects the user's `prefers-reduced-motion` setting.
 */

'use client';

import { useEffect, useRef, useState } from 'react';

// Max offset (in px) the pattern shifts away from the cursor center
const PARALLAX_OFFSET = 28;

interface PageBackgroundProps {
  /** Enable mouse-based parallax movement of the SVG pattern */
  parallax?: boolean;
}

export function PageBackground({ parallax = false }: PageBackgroundProps) {
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!parallax) return;

    // Respect users who prefer reduced motion
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduceMotion.matches) return;

    const handleMouseMove = (event: MouseEvent) => {
      if (frameRef.current !== null) return;
      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = null;
        // Normalize cursor position to [-1, 1] relative to viewport center
        const normalizedX = (event.clientX / window.innerWidth) * 2 - 1;
        const normalizedY = (event.clientY / window.innerHeight) * 2 - 1;
        // Move opposite to the cursor for a depth effect
        setOffset({
          x: -normalizedX * PARALLAX_OFFSET,
          y: -normalizedY * PARALLAX_OFFSET,
        });
      });
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [parallax]);

  return (
    <div className="absolute top-0 left-0 w-full h-[300px] pointer-events-none overflow-hidden bg-gradient-main">
      <div
        className="absolute -inset-12 will-change-transform"
        style={{
          backgroundImage: 'url(/images/banners/background.svg)',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'top center',
          transform: `translate3d(${offset.x}px, ${offset.y}px, 0)`,
          transition: 'transform 300ms ease-out',
        }}
      />
    </div>
  );
}
